import React, { useState, useEffect } from "react"
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from "@mui/material"
import { useTranslation } from "react-i18next"

type CashOpenCloseFormProps = {
    open: boolean
    onClose: () => void
    onSubmit: (data: any) => void | Promise<void>
    loading: boolean
    mode: "open" | "close"
    outletId?: string
    registerId?: string
    session?: any
}

function CashOpenCloseForm({
    open,
    onClose,
    onSubmit,
    loading,
    mode,
    outletId,
    registerId,
    session,
}: CashOpenCloseFormProps) {
    const { t } = useTranslation()
    const [amount, setAmount] = useState("")

    useEffect(() => {
        if (open) {
            setAmount("")
        }
    }, [open])

    function handleSubmit() {
        const amountMinor = Math.round(parseFloat(amount || "0") * 100)

        if (mode === "open") {
            onSubmit({
                outletId,
                registerId,
                openingFloatMinor: amountMinor,
                currency: "EUR",
            })
        } else {
            onSubmit({
                countedCashMinor: amountMinor,
            })
        }
    }

    const isValid = amount && parseFloat(amount) >= 0

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    m: { xs: 2, sm: 3 },
                },
            }}
        >
            <DialogTitle sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
                {mode === "open"
                    ? t("cashOpenCloseForm.openTitle")
                    : t("cashOpenCloseForm.closeTitle")}
            </DialogTitle>
            <DialogContent>
                {mode === "open" && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 3, fontSize: { xs: "0.875rem", sm: "1rem" } }}
                    >
                        {t("cashOpenCloseForm.openHelp")}
                    </Typography>
                )}

                {mode === "close" && session && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 3, fontSize: { xs: "0.875rem", sm: "1rem" } }}
                    >
                        {t("cashOpenCloseForm.closeHelp")}
                    </Typography>
                )}

                <TextField
                    fullWidth
                    label={
                        mode === "open"
                            ? String(t("cashOpenCloseForm.openingFloat"))
                            : String(t("cashOpenCloseForm.countedCash"))
                    }
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    disabled={loading}
                    inputMode="decimal"
                    autoFocus
                    sx={{
                        "& .MuiInputBase-root": {
                            minHeight: 48,
                            fontSize: { xs: "1rem", sm: "1rem" },
                        },
                    }}
                />
            </DialogContent>
            <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
                <Button
                    onClick={onClose}
                    disabled={loading}
                    sx={{
                        minHeight: 48,
                        px: 3,
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                    }}
                >
                    {t("common.cancel")}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!isValid || loading}
                    color={mode === "close" ? "error" : "primary"}
                    sx={{
                        minHeight: 48,
                        px: 3,
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                    }}
                >
                    {mode === "open"
                        ? t("cashOpenCloseForm.openAction")
                        : t("cashOpenCloseForm.closeAction")}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default CashOpenCloseForm
